<template>
  <div class="pt-4 pb-4 px-6">
    <div v-show="!showingAdd">
      <SecondaryButton @click="showAdd" class="w-full" :size="'lg'">Add New Address</SecondaryButton>
    </div>
    <fieldset v-show="showingAdd">
      <div>
        <div class="flex justify-between">
          <legend class="block text-md font-semibold tracking-tighter leading-5 text-gray-brand-900">
            {{ title }}
          </legend>
          <button
            v-if="passedDeliveryAddress"
            class="ml-2 inline-flex items-center shadow-sm p-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-full bg-white hover:border-blue-600 transition-all duration-150 group-hover:opacity-100 text-gray-500 hover:text-blue-600"
            @click="updateCloseClicked()"
          >
            <XIcon class="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <legend class="block mt-1 mb-3 text-sm tracking-wide leading-5 text-gray-brand-800">
          Enter your address details.
        </legend>
      </div>

      <form class="mt-2 bg-white rounded-md shadow-sm">
        <!-- Manual Address Entry -->
        <div class="-mt-px flex">
          <div class="w-3/5 flex-1 min-w-0">
            <input
              id="address-line1"
              ref="addr1"
              v-model="address1"
              type="text"
              name="address-line1"
              autocomplete="address-line1"
              autocapitalize="words"
              aria-label="Street address"
              class="form-input relative block w-full rounded-none rounded-tl-lg bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
              placeholder="Street address"
            />
          </div>
          <div class="w-2/5 -ml-px min-w-0">
            <input
              id="address-line2"
              ref="addr2"
              v-model="address2"
              type="text"
              autocomplete="address-line2"
              name="address-line2"
              aria-label="Unit"
              class="form-input relative block w-full rounded-none rounded-tr-lg bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
              placeholder="Unit"
            />
          </div>
        </div>
        <div class="-mt-px flex">
          <div class="w-3/10 sm:w-2/5 flex-1 min-w-0">
            <input
              id="address-level2"
              v-model="city"
              type="text"
              autocomplete="address-level2"
              autocapitalize="words"
              name="address-level2"
              aria-label="City"
              class="form-input relative block w-full rounded-none bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
              placeholder="City"
            />
          </div>
          <div class="w-3/10 sm:w-1/5 -ml-px min-w-0">
            <select
              id="state"
              v-model="state"
              :key="`state-${merchantCountry}-${state}`"
              autocomplete="address-level1"
              name="state"
              class="form-select relative block w-full rounded-none bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
              placeholder="State"
              @change="onStateChange"
            >
              <option value="" disabled selected>{{ statePlaceholder }}</option>
              <template v-if="merchantCountry === 'USA'">
                <option value="AK">AK</option>
                <option value="AZ">AZ</option>
                <option value="AL">AL</option>
                <option value="AR">AR</option>
                <option value="CA">CA</option>
                <option value="CO">CO</option>
                <option value="CT">CT</option>
                <option value="DC">DC</option>
                <option value="DE">DE</option>
                <option value="FL">FL</option>
                <option value="GA">GA</option>
                <option value="HI">HI</option>
                <option value="ID">ID</option>
                <option value="IL">IL</option>
                <option value="IN">IN</option>
                <option value="IA">IA</option>
                <option value="KS">KS</option>
                <option value="KY">KY</option>
                <option value="LA">LA</option>
                <option value="ME">ME</option>
                <option value="MD">MD</option>
                <option value="MA">MA</option>
                <option value="MI">MI</option>
                <option value="MN">MN</option>
                <option value="MS">MS</option>
                <option value="MO">MO</option>
                <option value="MT">MT</option>
                <option value="NE">NE</option>
                <option value="NV">NV</option>
                <option value="NH">NH</option>
                <option value="NJ">NJ</option>
                <option value="NM">NM</option>
                <option value="NY">NY</option>
                <option value="NC">NC</option>
                <option value="ND">ND</option>
                <option value="OH">OH</option>
                <option value="OK">OK</option>
                <option value="OR">OR</option>
                <option value="PA">PA</option>
                <option value="RI">RI</option>
                <option value="SC">SC</option>
                <option value="SD">SD</option>
                <option value="TN">TN</option>
                <option value="TX">TX</option>
                <option value="UT">UT</option>
                <option value="VT">VT</option>
                <option value="VA">VA</option>
                <option value="WA">WA</option>
                <option value="WV">WV</option>
                <option value="WI">WI</option>
                <option value="WY">WY</option>
              </template>
              <template v-if="merchantCountry === 'AUS'">
                <option value="ACT">ACT</option>
                <option value="NSW">NSW</option>
                <option value="NT">NT</option>
                <option value="QLD">QLD</option>
                <option value="SA">SA</option>
                <option value="TAS">TAS</option>
                <option value="VIC">VIC</option>
                <option value="WA">WA</option>
              </template>
              <template v-if="merchantCountry === 'CAN'">
                <option value="AB">AB</option>
                <option value="BC">BC</option>
                <option value="MB">MB</option>
                <option value="NB">NB</option>
                <option value="NL">NL</option>
                <option value="NS">NS</option>
                <option value="NT">NT</option>
                <option value="NU">NU</option>
                <option value="ON">ON</option>
                <option value="PE">PE</option>
                <option value="QC">QC</option>
                <option value="SK">SK</option>
                <option value="YT">YT</option>
              </template>
              <template v-if="merchantCountry === 'GBR'">
                <option value="BED">Bedfordshire</option>
                <option value="BERK">Berkshire</option>
                <option value="BUCK">Buckinghamshire</option>
                <option value="CAM">Cambridgeshire</option>
                <option value="CHES">Cheshire</option>
                <option value="CORN">Cornwall</option>
                <option value="CUMB">Cumbria</option>
                <option value="DERB">Derbyshire</option>
                <option value="DEV">Devon</option>
                <option value="DOR">Dorset</option>
                <option value="DUR">County Durham</option>
                <option value="ERYORK">East Riding of Yorkshire</option>
                <option value="ESX">East Sussex</option>
                <option value="ESS">Essex</option>
                <option value="GLS">Gloucestershire</option>
                <option value="GMAN">Greater Manchester</option>
                <option value="HANTS">Hampshire</option>
                <option value="HERTS">Hertfordshire</option>
                <option value="HANTS">Herefordshire</option>
                <option value="IOW">Isle of Wight</option>
                <option value="KENT">Kent</option>
                <option value="LANCS">Lancashire</option>
                <option value="LEICS">Leicestershire</option>
                <option value="LINCS">Lincolnshire</option>
                <option value="LOND">Greater London</option>
                <option value="MER">Merseyside</option>
                <option value="NORF">Norfolk</option>
                <option value="NHANTS">Northamptonshire</option>
                <option value="NTHUMB">Northumberland</option>
                <option value="NOTTS">Nottinghamshire</option>
                <option value="OXON">Oxfordshire</option>
                <option value="RUT">Rutland</option>
                <option value="SHROP">Shropshire</option>
                <option value="SOM">Somerset</option>
                <option value="STS">Staffordshire</option>
                <option value="SUFF">Suffolk</option>
                <option value="SURREY">Surrey</option>
                <option value="TYNE">Tyne and Wear</option>
                <option value="WARKS">Warwickshire</option>
                <option value="W MIDS">West Midlands</option>
                <option value="W SUSX">West Sussex</option>
                <option value="WILTS">Wiltshire</option>
                <option value="WORCS">Worcestershire</option>
                <option value="YORK">North Yorkshire</option>
                <option value="SYORK">South Yorkshire</option>
                <option value="WYORK">West Yorkshire</option>
                <option value="WALES">Wales</option>
                <option value="SCOT">Scotland</option>
                <option value="NIRE">Northern Ireland</option>
              </template>
            </select>
          </div>
          <div class="w-2/5 -ml-px min-w-0">
            <input
              id="postal-code"
              v-model="zip"
              type="text"
              autocomplete="postal-code"
              name="postal-code"
              aria-label="Postal code"
              class="form-input relative block w-full rounded-none bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
              placeholder="Postal code"
            />
          </div>
        </div>
        <div class="-mt-px flex">
          <div class="w-full flex-1 min-w-0">
            <input
              id="notes"
              v-model="notes"
              type="text"
              autocomplete="off"
              name="notes"
              aria-label="Notes"
              class="form-input relative block w-full rounded-none rounded-bl-md rounded-br-md bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
              placeholder="Gate code or instructions (optional)"
            />
          </div>
        </div>
      </form>

      <div v-if="hasErrorMessage" class="mt-2 text-sm text-red-600">
        {{ errorMessage }}
      </div>

      <div class="mt-4 flex justify-end space-x-3">
        <SecondaryButton @click="updateCloseClicked" :size="'md'">Cancel</SecondaryButton>
        <PrimaryButton @click="addAddress" :disabled="!validAddress || isAddingAddress" :size="'md'" class="min-w-24">
          <span v-if="isAddingAddress">Saving...</span>
          <span v-else>{{ selectButtonTitle }}</span>
        </PrimaryButton>
      </div>
    </fieldset>
  </div>
</template>

<script>
import { XIcon } from '@heroicons/vue/solid'
import { nextTick } from 'vue'
import SecondaryButton from '@/components/Molecules/SecondaryButton'
import PrimaryButton from '@/components/Molecules/PrimaryButton'
import mapRelationship from '@/utils/mapRelationship'

export default {
  name: 'AddDeliveryAddressForm',
  components: {
    SecondaryButton,
    PrimaryButton,
    XIcon
  },
  props: {
    passedDeliveryAddress: {
      type: Object,
      required: false
    },
    forceOpen: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: () => ({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
    isAddingAddress: false,
    errorMessage: '',
    replacingAddressId: undefined,
    showingAdd: false
  }),
  computed: {
    statePlaceholder() {
      if (this.merchantCountry === 'USA') return 'State'
      if (this.merchantCountry === 'GBR') return 'County'
      return 'Province'
    },
    selectButtonTitle() {
      if (this.passedDeliveryAddress) {
        return 'Update & Save'
      } else {
        return 'Add & Save'
      }
    },
    title() {
      if (this.passedDeliveryAddress) {
        return 'Update an address'
      } else {
        return 'Add an address'
      }
    },
    validAddress() {
      return (
        this.address1 &&
        this.city &&
        this.state &&
        this.zip.length >= { USA: 5, AUS: 4, CAN: 7, GBR: 7 }[this.merchantCountry]
      )
    },
    hasErrorMessage() {
      return this.errorMessage.length > 0
    },
    merchantCountry() {
      return this.$store.getters.merchant?.attributes?.country || 'USA'
    }
  },
  watch: {
    state(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.errorMessage = ''
      }
    },
    passedDeliveryAddress: {
      handler(newValue) {
        if (newValue) {
          this.address1 = newValue.attributes.address1 || ''
          this.address2 = newValue.attributes.address2 || ''
          this.city = newValue.attributes.city || ''
          this.state = newValue.attributes.state || ''
          this.zip = newValue.attributes.zip || ''
          this.notes = newValue.attributes.notes || ''
          this.replacingAddressId = newValue.id
          this.showingAdd = true
        }
      },
      immediate: true
    },
    forceOpen: {
      handler(newValue) {
        if (newValue) {
          this.showingAdd = true
          nextTick(() => this.$refs.addr1?.focus())
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.showingAdd = this.forceOpen
    if (window.google && window.google.maps) {
      this.initializeGooglePlaces()
    }
  },
  methods: {
    initializeGooglePlaces() {
      google.maps.importLibrary('places').then(({ Autocomplete }) => {
        const autocomplete = new Autocomplete(this.$refs.addr1, {
          componentRestrictions: { country: { USA: 'us', AUS: 'au', CAN: 'ca', GBR: 'gb' }[this.merchantCountry] },
          fields: ['address_components', 'formatted_address', 'geometry', 'place_id']
        })
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace()
          console.log('Place selected:', place)

          this.address1 = ''
          this.address2 = ''
          this.city = ''
          this.state = ''
          this.zip = ''

          for (const comp of place.address_components) {
            console.log('Processing component:', comp.long_name, 'types:', comp.types)
            for (const type of comp.types) {
              switch (type) {
                case 'street_number':
                  this.address1 = comp.long_name
                  break
                case 'route':
                  if (this.address1) {
                    this.address1 += ' ' + comp.short_name
                  } else {
                    this.address1 = comp.short_name
                  }
                  break
                case 'subpremise':
                  this.address2 = comp.long_name
                  break
                case 'locality':
                  if (!this.city) {
                    this.city = comp.long_name
                  }
                  break
                case 'sublocality_level_1':
                  if (!this.city) {
                    this.city = comp.long_name
                  }
                  break
                case 'postal_town':
                  if (!this.city) {
                    this.city = comp.long_name
                  }
                  break
                case 'administrative_area_level_1':
                  if (this.merchantCountry === 'GBR') {
                    const mappedState = this.mapUKCountyToCode(comp.long_name)
                    console.log('Mapping UK county:', comp.long_name, 'to:', mappedState)
                    if (mappedState !== null) {
                      this.state = mappedState
                      console.log('State set to:', this.state)
                    }
                  } else {
                    this.state = comp.short_name
                  }
                  break
                case 'administrative_area_level_2':
                  if (this.merchantCountry === 'GBR' && !this.state) {
                    const mappedState = this.mapUKCountyToCode(comp.long_name)
                    if (mappedState !== null) {
                      this.state = mappedState
                    }
                  }
                  break
                case 'country':
                  break
                case 'postal_code':
                  this.zip = comp.long_name
                  break
              }
            }
          }

          if (!this.city && place.formatted_address) {
            console.log('No city found in components, trying formatted address:', place.formatted_address)
            const parts = place.formatted_address.split(',')
            if (parts.length > 1) {
              this.city = parts[1].trim()
              console.log('Extracted city from formatted address:', this.city)
            }
          }

          nextTick(() => this.$refs.addr2?.focus())
        })
      })
    },

    onStateChange() {
      console.log('State dropdown changed to:', this.state)
    },
    showAdd() {
      this.showingAdd = true
      nextTick(() => this.$refs.addr1?.focus())
    },
    addAddress() {
      this.isAddingAddress = true
      this.errorMessage = ''

      this.$store
        .dispatch('setCustomerDeliveryAddress', {
          address1: this.address1,
          address2: this.address2,
          city: this.city,
          addressState: this.state,
          zip: this.zip,
          notes: this.notes,
          country: this.merchantCountry,
          replacingAddressId: this.replacingAddressId
        })
        .then(
          (response) => {
            this.isAddingAddress = false
            this.clearFields()
            this.showingAdd = false
            this.$emit('delivery-address-saved')
          },
          (error) => {
            this.isAddingAddress = false
            this.errorMessage = 'Failed to save address. Please try again.'
          }
        )
    },
    clearFields() {
      this.address1 = ''
      this.address2 = ''
      this.city = ''
      this.state = ''
      this.zip = ''
      this.notes = ''
      this.replacingAddressId = undefined
    },

    mapUKCountyToCode(countyName) {
      const countyMap = {
        'Greater London': 'England',
        'West Midlands': 'England',
        'Greater Manchester': 'England',
        'West Yorkshire': 'England',
        Merseyside: 'England',
        'South Yorkshire': 'England',
        'Tyne and Wear': 'England',
        Nottinghamshire: 'England',
        Hampshire: 'England',
        Essex: 'England',
        Kent: 'England',
        Lancashire: 'England',
        Cheshire: 'England',
        Staffordshire: 'England',
        Derbyshire: 'England',
        Leicestershire: 'England',
        Somerset: 'England',
        Norfolk: 'England',
        Suffolk: 'England',
        Cambridgeshire: 'England',
        Oxfordshire: 'England',
        Gloucestershire: 'England',
        Warwickshire: 'England',
        Buckinghamshire: 'England',
        Hertfordshire: 'England',
        Bedfordshire: 'England',
        Berkshire: 'England',
        Surrey: 'England',
        Sussex: 'England',
        Dorset: 'England',
        Devon: 'England',
        Cornwall: 'England',
        Wiltshire: 'England',
        Hampshire: 'England',
        'Isle of Wight': 'England',
        Cumbria: 'England',
        Northumberland: 'England',
        Durham: 'England',
        'North Yorkshire': 'England',
        'East Riding of Yorkshire': 'England',
        'South Yorkshire': 'England',
        'West Yorkshire': 'England',
        Lincolnshire: 'England',
        Nottinghamshire: 'England',
        Derbyshire: 'England',
        Leicestershire: 'England',
        Rutland: 'England',
        Northamptonshire: 'England',
        Cambridgeshire: 'England',
        Norfolk: 'England',
        Suffolk: 'England',
        Essex: 'England',
        Hertfordshire: 'England',
        Bedfordshire: 'England',
        Buckinghamshire: 'England',
        Oxfordshire: 'England',
        Berkshire: 'England',
        Surrey: 'England',
        Kent: 'England',
        'East Sussex': 'England',
        'West Sussex': 'England',
        Hampshire: 'England',
        'Isle of Wight': 'England',
        Dorset: 'England',
        Somerset: 'England',
        Wiltshire: 'England',
        Gloucestershire: 'England',
        Herefordshire: 'England',
        Worcestershire: 'England',
        Warwickshire: 'England',
        Northamptonshire: 'England',
        Leicestershire: 'England',
        Rutland: 'England',
        Lincolnshire: 'England',
        Nottinghamshire: 'England',
        Derbyshire: 'England',
        Staffordshire: 'England',
        Shropshire: 'England',
        Cheshire: 'England',
        'Greater Manchester': 'England',
        Lancashire: 'England',
        Merseyside: 'England',
        Cumbria: 'England',
        Northumberland: 'England',
        'Tyne and Wear': 'England',
        Durham: 'England',
        'North Yorkshire': 'England',
        'East Riding of Yorkshire': 'England',
        'South Yorkshire': 'England',
        'West Yorkshire': 'England',
        'Greater London': 'England',
        'City of London': 'England',
        Bristol: 'England',
        'Bath and North East Somerset': 'England',
        'North Somerset': 'England',
        'South Gloucestershire': 'England',
        Swindon: 'England',
        'Milton Keynes': 'England',
        Peterborough: 'England',
        Luton: 'England',
        Bedford: 'England',
        'Central Bedfordshire': 'England',
        Hertfordshire: 'England',
        Essex: 'England',
        'Southend-on-Sea': 'England',
        Thurrock: 'England',
        Medway: 'England',
        Kent: 'England',
        'East Sussex': 'England',
        'Brighton and Hove': 'England',
        'West Sussex': 'England',
        Surrey: 'England',
        Hampshire: 'England',
        Portsmouth: 'England',
        Southampton: 'England',
        'Isle of Wight': 'England',
        Dorset: 'England',
        Bournemouth: 'England',
        Poole: 'England',
        Somerset: 'England',
        'Bath and North East Somerset': 'England',
        'North Somerset': 'England',
        Wiltshire: 'England',
        Swindon: 'England',
        Gloucestershire: 'England',
        'South Gloucestershire': 'England',
        Bristol: 'England',
        Herefordshire: 'England',
        Worcestershire: 'England',
        Warwickshire: 'England',
        Northamptonshire: 'England',
        Leicestershire: 'England',
        Rutland: 'England',
        Lincolnshire: 'England',
        Nottinghamshire: 'England',
        Nottingham: 'England',
        Derbyshire: 'England',
        Derby: 'England',
        Staffordshire: 'England',
        'Stoke-on-Trent': 'England',
        Shropshire: 'England',
        'Telford and Wrekin': 'England',
        Cheshire: 'England',
        'Cheshire East': 'England',
        'Cheshire West and Chester': 'England',
        Halton: 'England',
        Warrington: 'England',
        'Greater Manchester': 'England',
        Lancashire: 'England',
        Blackpool: 'England',
        'Blackburn with Darwen': 'England',
        Merseyside: 'England',
        Liverpool: 'England',
        Knowsley: 'England',
        'St. Helens': 'England',
        Sefton: 'England',
        Wirral: 'England',
        Cumbria: 'England',
        Northumberland: 'England',
        'Tyne and Wear': 'England',
        'Newcastle upon Tyne': 'England',
        Gateshead: 'England',
        'South Tyneside': 'England',
        Sunderland: 'England',
        Durham: 'England',
        Darlington: 'England',
        Hartlepool: 'England',
        'Stockton-on-Tees': 'England',
        Middlesbrough: 'England',
        'Redcar and Cleveland': 'England',
        'North Yorkshire': 'England',
        York: 'England',
        'East Riding of Yorkshire': 'England',
        'Kingston upon Hull': 'England',
        'South Yorkshire': 'England',
        Sheffield: 'England',
        Rotherham: 'England',
        Doncaster: 'England',
        Barnsley: 'England',
        'West Yorkshire': 'England',
        Leeds: 'England',
        Wakefield: 'England',
        Kirklees: 'England',
        Calderdale: 'England',
        Bradford: 'England',
        'Greater London': 'England',
        'City of London': 'England',
        Westminster: 'England',
        'Kensington and Chelsea': 'England',
        'Hammersmith and Fulham': 'England',
        Wandsworth: 'England',
        Lambeth: 'England',
        Southwark: 'England',
        'Tower Hamlets': 'England',
        Hackney: 'England',
        Islington: 'England',
        Camden: 'England',
        Brent: 'England',
        Ealing: 'England',
        Hounslow: 'England',
        'Richmond upon Thames': 'England',
        'Kingston upon Thames': 'England',
        Merton: 'England',
        Sutton: 'England',
        Croydon: 'England',
        Bromley: 'England',
        Lewisham: 'England',
        Greenwich: 'England',
        Bexley: 'England',
        Havering: 'England',
        'Barking and Dagenham': 'England',
        Redbridge: 'England',
        Newham: 'England',
        'Waltham Forest': 'England',
        Haringey: 'England',
        Enfield: 'England',
        Barnet: 'England',
        Harrow: 'England',
        Hillingdon: 'England',
        Scotland: 'Scotland',
        Wales: 'Wales',
        'Northern Ireland': 'Northern Ireland'
      }

      return countyMap[countyName] || null
    },
    updateCloseClicked() {
      this.clearFields()
      this.showingAdd = false
      this.$emit('delivery-address-edit-canceled')
    }
  }
}
</script>
